// CodebaseDetails.js
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import codebaseService from "../../services/codebaseService";
import RunHistoryList from "./RunHistoryList";

const CodebaseDetails = () => {
  const { codebaseId } = useParams();
  const navigate = useNavigate();

  const [codebaseDetails, setCodebaseDetails] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    modelType: "",
  });

  // Handle input updates
  function handleChange(e) {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  }

  // 🔵 REFETCH function extracted so we can call it after saving
  const fetchCodebaseDetails = async () => {
    try {
      const codebaseData = await codebaseService.getCodebase(codebaseId);
      setCodebaseDetails(codebaseData);
      setFormData({
        title: codebaseData.codebase_name,
        description: codebaseData.codebase_desc,
        modelType: codebaseData.modelType,
      });
    } catch (error) {
      console.error("Error fetching codebase details:", error);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCodebaseDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codebaseId]);

  // 🔵 Save & Refresh
  const changeCodebaseDetails = async (e) => {
    e.preventDefault();

    try {
      await codebaseService.changeCodebaseDetails(
        codebaseId,
        formData.title,
        formData.description,
        formData.modelType
      );

      Swal.fire("Codebase Details Updated!", "", "success").then(() => {
        fetchCodebaseDetails(); // 🔵 Refresh page data
      });

    } catch (error) {
      console.error("Error modifying codebase details:", error);
      Swal.fire("Error!", "Unable to update codebase.", "error");
    }
  };

  if (!codebaseDetails) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
        <p className="text-sm sm:text-base text-[#64748B]">Loading codebase details...</p>
      </div>
    );
  }

  const accuracyLabel =
    codebaseDetails.tensorflowAccuracy != null
      ? `${codebaseDetails.tensorflowAccuracy}%`
      : "N/A";

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-10">

        {/* TOP BAR */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center justify-center rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-xs sm:text-sm font-medium text-[#0F172A] shadow-sm hover:bg-[#E2E8F0] transition"
          >
            ← Back
          </button>

          <p className="hidden sm:block text-xs text-[#94A3B8]">
            Codebase / <span className="text-[#0F172A]">Details</span>
          </p>
        </div>

        {/* HERO HEADER */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#0EA5E9] via-[#38BDF8] to-[#6366F1] px-5 sm:px-7 py-6 sm:py-7 mb-8 shadow-md">
          <div className="absolute inset-y-0 right-[-80px] sm:right-[-40px] w-56 sm:w-72 opacity-40 pointer-events-none bg-[radial-gradient(circle_at_top,_white,_transparent_60%)]" />

          <div className="relative z-10 flex flex-col gap-3 sm:gap-4">
            <div className="flex items-center gap-2 text-[0.65rem] sm:text-xs font-semibold tracking-wide text-sky-950/90 uppercase">
              <span className="bg-white/80 px-2 py-1 rounded-full">Codebase</span>
              <span className="bg-white/30 px-2 py-1 rounded-full text-white border border-white/40">
                ID: {codebaseId}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white leading-tight">
              {codebaseDetails.codebase_name || "Untitled Codebase"}
            </h1>

            {codebaseDetails.codebase_desc && (
              <p className="text-sm sm:text-base text-sky-50/90 max-w-2xl">
                {codebaseDetails.codebase_desc}
              </p>
            )}

            <div className="flex flex-wrap gap-2 mt-1">
              <span className="inline-flex items-center rounded-full bg-sky-800/40 border border-white/40 px-3 py-1 text-xs text-sky-50">
                Model:{" "}
                <span className="ml-1 font-semibold">{codebaseDetails.modelType || "N/A"}</span>
              </span>

              <span className="inline-flex items-center rounded-full bg-sky-800/40 border border-white/40 px-3 py-1 text-xs text-sky-50">
                TF Accuracy:{" "}
                <span className="ml-1 font-semibold">{accuracyLabel}</span>
              </span>
            </div>
          </div>
        </div>

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6 lg:gap-8">

          {/* LEFT FORM CARD */}
          <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 lg:p-7 shadow-sm">
            <h2 className="text-lg sm:text-xl font-bold text-[#003153] mb-4">
              Edit Codebase
            </h2>

            <form className="space-y-4" onSubmit={changeCodebaseDetails}>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs sm:text-sm font-semibold text-[#475569]">Title</label>
                <input
                  name="title"
                  defaultValue={codebaseDetails.codebase_name}
                  onChange={handleChange}
                  className="border border-[#CBD5E1] rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#0EA5E9]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs sm:text-sm font-semibold text-[#475569]">Description</label>
                <textarea
                  name="description"
                  defaultValue={codebaseDetails.codebase_desc}
                  onChange={handleChange}
                  className="border border-[#CBD5E1] rounded-xl px-3 py-2.5 text-sm bg-white h-32 resize-none focus:ring-2 focus:ring-[#0EA5E9]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs sm:text-sm font-semibold text-[#475569]">Model Type</label>
                <select
                  name="modelType"
                  onChange={handleChange}
                  className="border border-[#CBD5E1] rounded-xl px-3 py-2.5 bg-white text-sm focus:ring-2 focus:ring-[#0EA5E9]"
                >
                  {codebaseDetails.modelType === "Unsupervised" ? (
                    <>
                      <option value="Unsupervised">Unsupervised</option>
                      <option value="Supervised">Supervised</option>
                    </>
                  ) : (
                    <>
                      <option value="Supervised">Supervised</option>
                      <option value="Unsupervised">Unsupervised</option>
                    </>
                  )}
                </select>
              </div>

              <button
                type="submit"
                className="w-full mt-2 text-sm sm:text-base text-white font-semibold rounded-xl py-2.5 bg-[#0EA5E9] hover:bg-[#0284C7] transition-all duration-200 shadow-sm"
              >
                Save Changes
              </button>
            </form>
          </div>

          {/* RIGHT DETAILS */}
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-bold text-[#003153] mb-3">
                Summary
              </h2>

              <div className="space-y-3 text-sm text-[#475569]">
                <p>
                  <span className="font-semibold text-[#0F172A]">Codebase ID:</span>{" "}
                  <span className="font-mono text-xs bg-[#F8FAFC] px-2 py-1 rounded-lg border border-[#E2E8F0]">
                    {codebaseId}
                  </span>
                </p>

                <p>
                  <span className="font-semibold text-[#0F172A]">Model Type:</span>{" "}
                  {codebaseDetails.modelType || "N/A"}
                </p>

                <p>
                  <span className="font-semibold text-[#0F172A]">TensorFlow Accuracy:</span>{" "}
                  {accuracyLabel}
                </p>
              </div>
            </div>

            {/* Content Snapshots */}
            <div className="bg-white border border-[#E2E8F0] rounded-3xl p-5 sm:p-6 shadow-sm">
              <h2 className="text-lg sm:text-xl font-bold text-[#003153] mb-3">
                Content Snapshots
              </h2>

              <div className="space-y-4 text-sm text-[#475569]">
                <div>
                  <p className="font-semibold text-[#0F172A]">Gemini Content</p>
                  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 max-h-32 overflow-auto text-xs">
                    {codebaseDetails.geminiContent || "N/A"}
                  </div>
                </div>

                <div>
                  <p className="font-semibold text-[#0F172A]">Python Content</p>
                  <div className="rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 max-h-32 overflow-auto font-mono text-xs">
                    {codebaseDetails.pythonContent || "N/A"}
                  </div>
                </div>
              </div>
            </div>

            <RunHistoryList codebaseId={codebaseId} />

          </div>
        </div>

      </div>
    </div>
  );
};

export default CodebaseDetails;
